import React from "react";
import { useTranslation } from "react-i18next";

import {
  CompassColor,
  Heading,
  Image,
  List,
  ListItem,
  Spacing,
  Stack,
  Text,
} from "@noom/wax-component-library";

import type {
  QuestionComponentProps,
  QuestionDefinition,
} from "@/components/survey/Question";
import { BaseInterstitial } from "@/components/survey/questionTypes/core";
import { ProgramOffering } from "@/constants";
import { useAppContext } from "@/contexts";
import { useGoto } from "@/hooks";
import { getDisplayName } from "@/models";

const QUESTION_ID = "retriageIntro";

const PROGRAM_TRANSLATION_KEYS: Record<
  Exclude<ProgramOffering, "WEIGHT" | "MOOD">,
  string
> = {
  DPP: "dpp",
  DIABETES_LIFESTYLE: "diabetesLifestyle",
  MED: "med",
  NMPBA: "med",
};

export const RetriageIntro: React.FC<QuestionComponentProps> = ({
  onClickNext,
}) => {
  const { partnerInfo, retriageEligibility } = useAppContext();
  const { openApp } = useGoto();
  const { t } = useTranslation();

  const { eligibleRetriagePrograms = [] } = retriageEligibility || {};
  const partnerName = getDisplayName(partnerInfo);

  // Filter programs to only show those the member is eligible for
  const programTranslationKeys = Object.entries(PROGRAM_TRANSLATION_KEYS)
    .filter(([programOffering]) =>
      eligibleRetriagePrograms.includes(programOffering as ProgramOffering),
    )
    .map(([, translationKey]) => translationKey);

  const handleBack = () => {
    openApp();
  };

  return (
    <BaseInterstitial
      backgroundColor={CompassColor.blueberry}
      ctaText={t("retriageIntro.cta")}
      onClickBack={handleBack}
      onClickNext={onClickNext}
      questionId={QUESTION_ID}
      showBackButton
      invertBackButton
    >
      <Stack alignItems="center" spacing={Spacing[4]}>
        <Image alt="Program upgrade" src="/assets/img/program-upgrade.svg" />
        <Heading fontSize="20px" fontWeight={600} color={CompassColor.white}>
          {t("retriageIntro.title")}
        </Heading>

        <Text fontSize="14px" color={CompassColor.white}>
          {t("retriageIntro.description", { partnerName })}
        </Text>

        {programTranslationKeys.length > 0 && (
          <List
            styleType="disc"
            paddingLeft={Spacing[2]}
            fontSize="14px"
            color={CompassColor.white}
          >
            {programTranslationKeys.map((translationKey) => (
              <ListItem key={translationKey} marginTop={Spacing[1]}>
                <Text color={CompassColor.white} fontWeight={600}>
                  {t(`retriageIntro.programs.${translationKey}.name`)}
                </Text>
                {", "}
                <Text color={CompassColor.white}>
                  {t(`retriageIntro.programs.${translationKey}.description`)}
                </Text>
              </ListItem>
            ))}
          </List>
        )}
      </Stack>
    </BaseInterstitial>
  );
};

export const Q_RETRIAGE_INTRO: QuestionDefinition = {
  id: QUESTION_ID,
  shouldShowQuestion: ({ retriageEligibility }) =>
    retriageEligibility?.didVerifyUsingAutologinData === true,
  component: RetriageIntro,
};
